import React, { useEffect, useRef, useState } from "react";
import * as t from "io-ts";
import * as luxon from "luxon";
import { CRMCardOutsideLabelled } from "../../CRMCardOutsideLabelled/CRMCardOutsideLabelled";
import { CRMParagraph } from "../../Simple/CRMParagraph/CRMParagraph";
import { SpacingColumn } from "../../BuildingBlocks/SpacingColumn";
import { SpacingRow } from "../../BuildingBlocks/SpacingRow";
import { CRMSpacing } from "../../../models/CRMSpacing";
import { Padding } from "../../BuildingBlocks/Padding";
import { CRMIcon } from "../../CRMIcon/CRMIcon";
import { FontQuicksand } from "../../BuildingBlocks/FontQuicksand";
import { FontSize } from "../../BuildingBlocks/FontSize";
import { Text } from "../../BuildingBlocks/Text";
import { Uppercase } from "../../Uppercase/Uppercase";
import { WeightBold } from "../../WeightBold/WeightBold";
import { CRMFontSizes } from "../../../models/CRMFontSizes";
import { getJson, postJson } from "../../../wrappers/restRequest";
import * as FirstPartyFetchResponse from "../../../../../domain/models/FirstPartyFetchResponse";
import { getWaitingOnSubjectLink } from "./caseQuickSummaryLinkGenerators";

const formatGeneratedAt = (iso: string): string => {
    const dt = luxon.DateTime.fromISO(iso, { zone: "utc" }).setZone("Europe/London");
    if (!dt.isValid) {
        return iso;
    }
    return dt.toRelative() ?? dt.toFormat("d MMM yyyy");
};

const formatWrittenAt = (iso: string): string => {
    const dt = luxon.DateTime.fromISO(iso, { zone: "utc" }).setZone("Europe/London");
    if (!dt.isValid) {
        return iso;
    }
    return `Written ${dt.toFormat("d MMM yyyy 'at' HH:mm")}`;
};

const formatShortDate = (iso: string): string => {
    const dt = luxon.DateTime.fromISO(iso.replace(" ", "T"), { zone: "utc" }).setZone("Europe/London");
    if (!dt.isValid) {
        return iso;
    }
    return dt.toFormat("d MMM");
};

const ConfidenceCodec = t.union([t.literal("low"), t.literal("medium"), t.literal("high")]);

const WaitingOnOwnerCodec = t.union([
    t.literal("client"),
    t.literal("other_side"),
    t.literal("third_party"),
    t.literal("us"),
]);
type TWaitingOnOwner = t.TypeOf<typeof WaitingOnOwnerCodec>;

const WaitingOnSubjectCodec = t.union([
    t.type({ kind: t.literal("roadblock"), roadblock_id: t.string }),
    t.type({ kind: t.literal("enquiries") }),
    t.type({ kind: t.literal("documents") }),
    t.type({ kind: t.literal("ad_hoc_jobs") }),
    t.type({ kind: t.literal("inbox_emails") }),
    t.type({ kind: t.literal("complaint") }),
]);

const WaitingOnItemCodec = t.intersection([
    t.type({
        owner: WaitingOnOwnerCodec,
        label: t.string,
        detail: t.union([t.string, t.null]),
        due_date: t.union([t.string, t.null]),
        assignee_name: t.union([t.string, t.null]),
    }),
    t.partial({
        subject: WaitingOnSubjectCodec,
    }),
]);
type TWaitingOnItem = t.TypeOf<typeof WaitingOnItemCodec>;

const ActivityItemCodec = t.type({
    label: t.string,
    occurred_at: t.string,
    actor: t.union([t.string, t.null]),
});

const CompletedEventCodec = t.type({
    source_type: t.union([
        t.literal("roadblock"),
        t.literal("document"),
        t.literal("job"),
        t.literal("enquiry"),
        t.literal("milestone")
    ]),
    description: t.string,
    completed_at: t.string,
    completed_by_name: t.union([t.string, t.null]),
});
type TCompletedEvent = t.TypeOf<typeof CompletedEventCodec>;

const CommsContactCodec = t.type({
    role: t.string,
    sent_at: t.string,
});

const CommsCodec = t.type({
    digest: t.string,
    last_inbound: t.union([CommsContactCodec, t.null]),
    last_outbound: t.union([CommsContactCodec, t.null]),
});

const WhatsNextItemCodec = t.type({
    owner: t.union([WaitingOnOwnerCodec, t.null]),
    action: t.string,
});
type TWhatsNextItem = t.TypeOf<typeof WhatsNextItemCodec>;

const CaseQuickSummaryDataCodec = t.type({
    summary: t.union([
        t.null,
        t.type({
            id: t.string,
            case_id: t.string,
            headline: t.string,
            predicted_completion_month: t.union([t.string, t.null]),
            predicted_completion_confidence: t.union([ConfidenceCodec, t.null]),
            blocking: t.array(t.string),
            whats_next: t.array(t.string),
            waiting_on: t.array(WaitingOnItemCodec),
            recent_activity: t.array(ActivityItemCodec),
            comms_summary: t.union([CommsCodec, t.null]),
            whats_next_v2: t.union([t.array(WhatsNextItemCodec), t.null]),
            model_name: t.string,
            generated_at: t.string,
            is_stale: t.boolean,
        }),
    ]),
    recently_completed: t.array(CompletedEventCodec),
});

type TCaseQuickSummaryData = t.TypeOf<typeof CaseQuickSummaryDataCodec>;

type TLoadStatus = "idle" | "loading" | "ready" | "empty" | "error" | "refreshing";

const waitingOnOwnerOrder: Array<TWaitingOnOwner> = ["client", "other_side", "third_party", "us"];

const ownerShortLabel: Record<TWaitingOnOwner, string> = {
    client: "Client",
    other_side: "Other side",
    third_party: "Third party",
    us: "Us",
};

const OwnerTag = (props: { owner: TWaitingOnOwner }): JSX.Element =>
    <span className="crm-case-quick-summary-card__owner-tag">
        <span className={`crm-case-quick-summary-card__owner-dot crm-case-quick-summary-card__owner-dot--${props.owner}`} />
        <span className="crm-case-quick-summary-card__owner-word">{ownerShortLabel[props.owner]}</span>
    </span>;

const formatWaitingOnMeta = (item: TWaitingOnItem): string => {
    const parts: Array<string> = [];
    if (item.due_date !== null) {
        parts.push(formatShortDate(item.due_date));
    }
    if (item.assignee_name !== null && item.assignee_name !== "") {
        parts.push(item.assignee_name);
    }
    return parts.join(" · ");
};

const WaitingOnLabel = (props: { caseId: string; item: TWaitingOnItem }): JSX.Element => {
    const link = getWaitingOnSubjectLink(props.caseId, props.item.subject);
    if (link === null) {
        return <>{props.item.label}</>;
    }
    return (
        <span
            className="crm-case-quick-summary-card__subject-link"
            role="link"
            tabIndex={0}
            title="Open in a new tab"
            onClick={() => window.open(link, "_blank")}
            onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                    window.open(link, "_blank");
                }
            }}
        >
            {props.item.label}
        </span>
    );
};

const SectionTitle = (props: { children: React.ReactNode }): JSX.Element =>
    <FontQuicksand>
        <Text size={CRMFontSizes.SMALL} lineHeight={1.2}>
            <WeightBold>{props.children}</WeightBold>
        </Text>
    </FontQuicksand>;

const sessionCache: Map<string, { status: TLoadStatus; data: TCaseQuickSummaryData }> = new Map();

const REGENERATION_POLL_DELAY_MS = 12000;

type TLoadKind = "initial" | "revalidate" | "regeneration-poll";

interface ICRMCaseQuickSummaryCardProps {
    caseId: string;
}

export const CRMCaseQuickSummaryCard = (props: ICRMCaseQuickSummaryCardProps): JSX.Element => {
    const cached = sessionCache.get(props.caseId);
    const [status, setStatus] = useState<TLoadStatus>(cached ? cached.status : "idle");
    const [summary, setSummary] = useState<TCaseQuickSummaryData | null>(cached ? cached.data : null);
    const [showActivity, setShowActivity] = useState<boolean>(false);

    const regenerationPoll = useRef<ReturnType<typeof setTimeout> | null>(null);

    const clearRegenerationPoll = (): void => {
        if (regenerationPoll.current !== null) {
            clearTimeout(regenerationPoll.current);
            regenerationPoll.current = null;
        }
    };

    useEffect(() => {
        let cancelled = false;

        const load = (kind: TLoadKind): void => {
            if (kind === "initial") {
                setStatus("loading");
            }
            getJson({
                url: `/v1/cases/${props.caseId}/quick-summary`,
                dataCodec: CaseQuickSummaryDataCodec,
                defaultData: { summary: null, recently_completed: [] },
            })().then((response) => {
                if (cancelled) {
                    return;
                }
                if (response.tag !== FirstPartyFetchResponse.constants.STATUS_2XX) {
                    if (kind === "initial") {
                        setStatus("error");
                    }
                    return;
                }
                const data = response.response.data;
                const nextStatus: TLoadStatus = data === null || data.summary === null ? "empty" : "ready";
                setSummary(data);
                setStatus(nextStatus);
                sessionCache.set(props.caseId, { status: nextStatus, data });

                if (kind !== "regeneration-poll" && (data === null || data.summary === null || data.summary.is_stale)) {
                    clearRegenerationPoll();
                    regenerationPoll.current = setTimeout(
                        () => load("regeneration-poll"),
                        REGENERATION_POLL_DELAY_MS,
                    );
                }
            });
        };

        load(sessionCache.has(props.caseId) ? "revalidate" : "initial");

        const onVisibilityChange = (): void => {
            if (document.visibilityState === "visible") {
                load("revalidate");
            }
        };
        document.addEventListener("visibilitychange", onVisibilityChange);

        return () => {
            cancelled = true;
            clearRegenerationPoll();
            document.removeEventListener("visibilitychange", onVisibilityChange);
        };
    }, [props.caseId]);

    const handleRefresh = (): void => {
        clearRegenerationPoll();
        setStatus("refreshing");
        postJson({
            url: `/v1/cases/${props.caseId}/quick-summary/refresh`,
            body: {},
            dataCodec: CaseQuickSummaryDataCodec,
            defaultData: { summary: null, recently_completed: [] },
        })().then((response) => {
            if (response.tag === FirstPartyFetchResponse.constants.STATUS_2XX) {
                const data = response.response.data;
                const nextStatus: TLoadStatus = data === null || data.summary === null ? "empty" : "ready";
                setSummary(data);
                setStatus(nextStatus);
                sessionCache.set(props.caseId, { status: nextStatus, data });
            } else {
                setStatus("error");
            }
        });
    };

    const labelColor = ((): "highlights-positive-green-2" | "highlights-pertinent-yellow-2" | "highlights-instructional-brick-2" | "neutral-6" => {
        if (status === "error") {
            return "highlights-instructional-brick-2";
        }
        if (summary === null || summary.summary === null) {
            return "neutral-6";
        }
        if (summary.summary.is_stale) {
            return "highlights-pertinent-yellow-2";
        }
        return "highlights-positive-green-2";
    })();

    const isBusy = status === "loading" || status === "refreshing";
    const titleSuffix = summary === null || summary.summary === null
        ? ""
        : summary.summary.is_stale
            ? ` (${formatGeneratedAt(summary.summary.generated_at)})`
            : " (up to date)";

    const whatsNextItems: Array<TWhatsNextItem> = summary === null || summary.summary === null
        ? []
        : summary.summary.whats_next_v2 !== null
            ? summary.summary.whats_next_v2
            : summary.summary.whats_next.map((action) => ({ owner: null, action }));

    return (
        <div className="crm-case-quick-summary-card">
            <CRMCardOutsideLabelled labelColor={labelColor} shadow={true}>
                <Padding spacing={CRMSpacing.MEDIUM}>
                    <SpacingColumn spacing={CRMSpacing.MEDIUM}>
                        {/* TITLE */}
                        <div className="crm-case-quick-summary-card__heading-row">
                            <SpacingRow
                                spacing={CRMSpacing.TINY}
                                title={summary === null || summary.summary === null ? undefined : formatWrittenAt(summary.summary.generated_at)}
                            >
                                <CRMIcon
                                    iconName="robot"
                                    size="small"
                                    colour="neutral-ink"
                                />
                                <FontQuicksand>
                                    <FontSize size={CRMFontSizes.SMALL} lineHeight="1.2">
                                        <Uppercase>
                                            <WeightBold>AI Estimate{titleSuffix}</WeightBold>
                                        </Uppercase>
                                    </FontSize>
                                </FontQuicksand>
                                {summary !== null && summary.summary !== null && summary.summary.is_stale && (
                                    <span className="crm-case-quick-summary-card__stale-pill">Out of date</span>
                                )}
                            </SpacingRow>
                            <button
                                type="button"
                                className="crm-case-quick-summary-card__refresh-icon"
                                onClick={handleRefresh}
                                disabled={isBusy}
                                aria-label={summary === null || summary.summary === null ? "Generate summary" : "Refresh summary"}
                                title={summary === null || summary.summary === null ? "Generate summary" : "Refresh summary"}
                            >
                                <CRMIcon
                                    iconName="repeat"
                                    size="x-small"
                                    colour={isBusy ? "neutral-6" : "neutral-ink"}
                                />
                            </button>
                        </div>

                        {status === "loading" && <CRMParagraph>Loading case summary…</CRMParagraph>}

                        {status === "error" && (
                            <CRMParagraph>Couldn't load case summary. Try again with the refresh icon.</CRMParagraph>
                        )}

                        {status === "empty" && (summary === null || summary.summary === null) && (
                            <CRMParagraph>No summary yet — click the refresh icon to generate one.</CRMParagraph>
                        )}

                        {summary !== null && (summary.summary !== null || summary.recently_completed.length > 0) && (
                            <SpacingColumn spacing={CRMSpacing.MEDIUM}>
                                {summary.summary !== null && summary.summary.predicted_completion_month && (
                                    <CRMParagraph>
                                        <WeightBold>Likely to complete:&nbsp;</WeightBold>
                                        {summary.summary.predicted_completion_month}
                                        {summary.summary.predicted_completion_confidence && (
                                            <span
                                                className={
                                                    "crm-case-quick-summary-card__confidence" +
                                                    " crm-case-quick-summary-card__confidence--" +
                                                    summary.summary.predicted_completion_confidence
                                                }
                                            >
                                                {summary.summary.predicted_completion_confidence} confidence
                                            </span>
                                        )}
                                    </CRMParagraph>
                                )}

                                {summary.summary !== null && (
                                    <CRMParagraph>{summary.summary.headline}</CRMParagraph>
                                )}

                                {/* RECENTLY COMPLETED — dynamic non-AI list */}
                                {summary.recently_completed && summary.recently_completed.length > 0 && (
                                    <SpacingColumn spacing={CRMSpacing.TINY}>
                                        <SectionTitle>Recently completed</SectionTitle>
                                        <SpacingColumn spacing={CRMSpacing.TINY}>
                                            {summary.recently_completed.map((item, i) => (
                                                <CRMParagraph key={`completed-${i}`}>
                                                    <span className="crm-case-quick-summary-card__completed-bullet">✓</span> {item.description}
                                                    <span className="crm-case-quick-summary-card__meta">
                                                        {" "}· {luxon.DateTime.fromISO(item.completed_at).toRelative()}
                                                        {item.completed_by_name ? ` by ${item.completed_by_name}` : ""}
                                                    </span>
                                                </CRMParagraph>
                                            ))}
                                        </SpacingColumn>
                                    </SpacingColumn>
                                )}

                                {/* WAITING ON — flat, owner-tagged rows sorted external-first */}
                                {summary.summary !== null && summary.summary.waiting_on.length > 0 && (
                                    <SpacingColumn spacing={CRMSpacing.TINY}>
                                        <SectionTitle>Blocked</SectionTitle>
                                        <SpacingColumn spacing={CRMSpacing.TINY}>
                                            {[...summary.summary.waiting_on]
                                                .sort((a, b) => waitingOnOwnerOrder.indexOf(a.owner) - waitingOnOwnerOrder.indexOf(b.owner))
                                                .map((item, i) => {
                                                    const meta = formatWaitingOnMeta(item);
                                                    return (
                                                        <CRMParagraph key={`waiting-${i}`}>
                                                            <OwnerTag owner={item.owner} /> <WaitingOnLabel caseId={props.caseId} item={item} />
                                                            {meta !== "" && (
                                                                <span className="crm-case-quick-summary-card__meta"> · {meta}</span>
                                                            )}
                                                        </CRMParagraph>
                                                    );
                                                })}
                                        </SpacingColumn>
                                    </SpacingColumn>
                                )}

                                {/* WHAT'S NEXT — owner-tagged actions */}
                                {summary.summary !== null && whatsNextItems.length > 0 && (
                                    <SpacingColumn spacing={CRMSpacing.TINY}>
                                        <SectionTitle>Do next</SectionTitle>
                                        <SpacingColumn spacing={CRMSpacing.TINY}>
                                            {whatsNextItems.map((item, i) => (
                                                <CRMParagraph key={`whatsnext-${i}`}>
                                                    {item.owner !== null ? <><OwnerTag owner={item.owner} /> </> : "· "}
                                                    {item.action}
                                                </CRMParagraph>
                                            ))}
                                        </SpacingColumn>
                                    </SpacingColumn>
                                )}

                                {/* RECENT COMMS — one-line digest + compact footer */}
                                {summary.summary !== null && summary.summary.comms_summary !== null && (
                                    <div className="crm-case-quick-summary-card__meta">
                                        {summary.summary.comms_summary.digest}
                                        {(summary.summary.comms_summary.last_inbound !== null || summary.summary.comms_summary.last_outbound !== null) && (
                                            <>
                                                {" "}
                                                {[
                                                    summary.summary.comms_summary.last_outbound !== null
                                                        ? `Out ${formatShortDate(summary.summary.comms_summary.last_outbound.sent_at)}`
                                                        : null,
                                                    summary.summary.comms_summary.last_inbound !== null
                                                        ? `In ${formatShortDate(summary.summary.comms_summary.last_inbound.sent_at)}`
                                                        : null,
                                                ].filter((part) => part !== null).join(" · ")}
                                            </>
                                        )}
                                    </div>
                                )}

                                {/* RECENT ACTIVITY — context, collapsed behind a toggle */}
                                {summary.summary !== null && summary.summary.recent_activity.length > 0 && (
                                    <SpacingColumn spacing={CRMSpacing.TINY}>
                                        <button
                                            type="button"
                                            className="crm-case-quick-summary-card__activity-toggle"
                                            onClick={() => setShowActivity((v) => !v)}
                                        >
                                            {showActivity ? "Hide" : "Show"} recent activity ({summary.summary.recent_activity.length})
                                        </button>
                                        {showActivity && (
                                            <SpacingColumn spacing={CRMSpacing.TINY}>
                                                {summary.summary.recent_activity.map((item, i) => (
                                                    <div className="crm-case-quick-summary-card__meta" key={`activity-${i}`}>
                                                        · {item.label} · {formatShortDate(item.occurred_at)}
                                                        {item.actor !== null && ` (${item.actor})`}
                                                    </div>
                                                ))}
                                            </SpacingColumn>
                                        )}
                                    </SpacingColumn>
                                )}
                            </SpacingColumn>
                        )}
                    </SpacingColumn>
                </Padding>
            </CRMCardOutsideLabelled>
        </div>
    );
};
